import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { User, Lock, MapPin, Package, FileText, Plus, Trash2, Edit2, Check, X, Eye, EyeOff, ChevronRight, Bell, Mail, Smartphone, Wifi } from 'lucide-react';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import Header from '../components/Header';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

// Push notification helpers
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function subscribeToPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Push notifications non supportées par ce navigateur');
  }
  
  const registration = await navigator.serviceWorker.register('/sw-push.js');
  await navigator.serviceWorker.ready;
  
  // Get VAPID public key from backend
  const { data } = await axios.get(`${API}/push/vapid-public-key`);
  const vapidKey = urlBase64ToUint8Array(data.public_key);
  
  // Subscribe
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: vapidKey
  });
  
  const subJson = subscription.toJSON();
  
  // Send subscription to backend
  await axios.post(`${API}/push/subscribe`, {
    endpoint: subJson.endpoint,
    keys: subJson.keys
  }, { withCredentials: true });
  
  return subscription;
}

async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.getRegistration('/sw-push.js');
  if (registration) {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  }
  await axios.delete(`${API}/push/unsubscribe`, { withCredentials: true });
}

const Account = () => {
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  
  // Address state
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState({ email: true, sms: false, push: false });
  const [savingNotif, setSavingNotif] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [addressForm, setAddressForm] = useState({
    label: 'home',
    street: '',
    city: '',
    postal_code: '',
    country: 'France',
    is_default: false
  });

  useEffect(() => {
    fetchData();
    setPushSupported('serviceWorker' in navigator && 'PushManager' in window);
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, addressesRes, ordersRes] = await Promise.all([
        axios.get(`${API}/user/profile`, { withCredentials: true }),
        axios.get(`${API}/user/addresses`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true })
      ]);
      setProfile(profileRes.data);
      setProfileForm({ name: profileRes.data.name || '', phone: profileRes.data.phone || '' });
      setNotifPrefs(profileRes.data.notification_prefs || { email: true, sms: false, push: false });
      setAddresses(addressesRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async () => {
    try {
      await axios.patch(`${API}/user/profile`, profileForm, { withCredentials: true });
      await fetchData();
      await checkAuth();
      setEditingProfile(false);
      toast.success(t('account.saveProfile'));
    } catch (error) {
      toast.error(t('actions.save'));
    }
  };

  const saveNotifPrefs = async (newPrefs) => {
    setSavingNotif(true);
    try {
      await axios.patch(`${API}/user/profile`, { notification_prefs: newPrefs }, { withCredentials: true });
      setNotifPrefs(newPrefs);
      toast.success(t('account.notifTitle'), { duration: 800 });
    } catch { toast.error('Erreur'); }
    finally { setSavingNotif(false); }
  };

  const toggleNotif = async (key) => {
    if (key === 'push') {
      setSavingNotif(true);
      try {
        if (!notifPrefs.push) {
          // Activating push
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            toast.error(t('account.pushNotSupported'));
            setSavingNotif(false);
            return;
          }
          await subscribeToPush();
          const newPrefs = { ...notifPrefs, push: true };
          setNotifPrefs(newPrefs);
          toast.success(t('account.pushNotif'), { duration: 800 });
        } else {
          // Deactivating push
          await unsubscribeFromPush();
          const newPrefs = { ...notifPrefs, push: false };
          setNotifPrefs(newPrefs);
          await saveNotifPrefs(newPrefs);
          toast.success(t('account.pushNotif'), { duration: 800 });
        }
      } catch (err) {
        toast.error(err.message || 'Erreur lors de la configuration push');
      } finally {
        setSavingNotif(false);
      }
    } else {
      const newPrefs = { ...notifPrefs, [key]: !notifPrefs[key] };
      setNotifPrefs(newPrefs);
      saveNotifPrefs(newPrefs);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { withCredentials: true });
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Mot de passe modifié');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement');
    }
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    try {
      if (editingAddress) {
        await axios.patch(`${API}/user/addresses/${editingAddress.address_id}`, addressForm, { withCredentials: true });
        toast.success('Adresse mise à jour');
      } else {
        await axios.post(`${API}/user/addresses`, addressForm, { withCredentials: true });
        toast.success('Adresse ajoutée');
      }
      await fetchData();
      setShowAddressDialog(false);
      setEditingAddress(null);
      setAddressForm({ label: 'home', street: '', city: '', postal_code: '', country: 'France', is_default: false });
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const deleteAddress = async (addressId) => {
    if (!window.confirm('Supprimer cette adresse ?')) return;
    try {
      await axios.delete(`${API}/user/addresses/${addressId}`, { withCredentials: true });
      await fetchData();
      toast.success('Adresse supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-secondary';
      case 'shipped': return 'bg-accent';
      case 'delivered': return 'bg-primary';
      default: return 'bg-muted';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed': return 'Confirmée';
      case 'shipped': return 'Expédiée';
      case 'delivered': return 'Livrée';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="px-6 sm:px-8 lg:px-12 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-charcoal font-heading mb-8">{t('account.title')}</h1>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList className="bg-white border-2 border-charcoal rounded-full p-1 w-full flex overflow-x-auto">
              <TabsTrigger value="profile" className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal min-w-0">
                <User className="w-4 h-4 sm:mr-2 shrink-0" /> <span className="hidden sm:inline">Profil</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal min-w-0">
                <Bell className="w-4 h-4 sm:mr-2 shrink-0" /> <span className="hidden sm:inline">Notifications</span>
              </TabsTrigger>
              <TabsTrigger value="addresses" className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal min-w-0">
                <MapPin className="w-4 h-4 sm:mr-2 shrink-0" /> <span className="hidden sm:inline">Adresses</span>
              </TabsTrigger>
              <TabsTrigger value="orders" className="flex-1 rounded-full data-[state=active]:bg-primary data-[state=active]:text-charcoal min-w-0">
                <Package className="w-4 h-4 sm:mr-2 shrink-0" /> <span className="hidden sm:inline">Commandes</span>
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="card-brutal">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-charcoal font-heading">Informations personnelles</h2>
                  {!editingProfile && (
                    <button
                      onClick={() => setEditingProfile(true)}
                      data-testid="edit-profile-btn"
                      className="btn-outline text-sm px-4 py-2 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" /> Modifier
                    </button>
                  )}
                </div>

                {editingProfile ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Nom</label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        data-testid="profile-name-input"
                        className="input-brutal w-full"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Téléphone</label>
                      <input
                        type="tel"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        data-testid="profile-phone-input"
                        className="input-brutal w-full"
                        placeholder={t('account.phone')}
                      />
                    </div>
                    <div className="flex gap-3">
                      <button onClick={updateProfile} className="btn-primary flex items-center gap-2">
                        <Check className="w-4 h-4" /> Sauvegarder
                      </button>
                      <button onClick={() => setEditingProfile(false)} className="btn-outline flex items-center gap-2">
                        <X className="w-4 h-4" /> Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      {profile?.picture ? (
                        <img src={profile.picture} alt={profile.name} className="w-16 h-16 rounded-full border-2 border-charcoal" />
                      ) : (
                        <div className="w-16 h-16 rounded-full border-2 border-charcoal bg-secondary flex items-center justify-center">
                          <User className="w-8 h-8 text-charcoal" />
                        </div>
                      )}
                      <div>
                        <p className="font-bold text-charcoal text-lg">{profile?.name}</p>
                        <p className="text-charcoal/70">{profile?.email}</p>
                        {profile?.phone && <p className="text-charcoal/70">{profile.phone}</p>}
                      </div>
                    </div>
                    <div className="pt-4 border-t-2 border-charcoal/10">
                      <p className="text-sm text-charcoal/60">
                        Compte créé le {formatDate(profile?.created_at)}
                      </p>
                      <p className="text-sm text-charcoal/60">
                        Connexion via {profile?.auth_provider === 'google' ? 'Google' : t('account.emailNotif')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Password section */}
                {profile?.auth_provider !== 'google' && (
                  <div className="mt-6 pt-6 border-t-2 border-charcoal/10">
                    <h3 className="font-bold text-charcoal mb-4">Sécurité</h3>
                    <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                      <DialogTrigger asChild>
                        <button data-testid="change-password-btn" className="btn-outline flex items-center gap-2">
                          <Lock className="w-4 h-4" /> Changer le mot de passe
                        </button>
                      </DialogTrigger>
                      <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-bold font-heading">Changer le mot de passe</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={changePassword} className="space-y-4 mt-4">
                          <div>
                            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">{t('account.currentPassword')}</label>
                            <div className="relative">
                              <input
                                type={showPasswords ? 'text' : 'password'}
                                value={passwordForm.current_password}
                                onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                                className="input-brutal w-full pr-12"
                                required
                              />
                              <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute right-4 top-1/2 -translate-y-1/2">
                                {showPasswords ? <EyeOff className="w-5 h-5 text-charcoal/40" /> : <Eye className="w-5 h-5 text-charcoal/40" />}
                              </button>
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">{t('account.newPassword')}</label>
                            <input
                              type={showPasswords ? 'text' : 'password'}
                              value={passwordForm.new_password}
                              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                              className="input-brutal w-full"
                              minLength={6}
                              required
                            />
                          </div>
                          <div>
                            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Confirmer</label>
                            <input
                              type={showPasswords ? 'text' : 'password'}
                              value={passwordForm.confirm_password}
                              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                              className="input-brutal w-full"
                              minLength={6}
                              required
                            />
                          </div>
                          <button type="submit" className="w-full btn-primary">{t('account.changePassword')}</button>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <div className="card-brutal">
                <h2 className="text-xl font-bold text-charcoal font-heading mb-2">Preferences de notification</h2>
                <p className="text-sm text-charcoal/60 mb-6">Choisissez comment etre notifie quand quelqu'un vous envoie un message via un PingMe. Ces reglages s'appliquent par defaut a tous vos PingMes, sauf s'ils ont un reglage specifique.</p>
                
                <div className="space-y-4">
                  {[
                    { key: 'email', icon: Mail, label: t('account.emailNotif'), desc: `Recevoir un email a ${profile?.email || '...'}`, available: true },
                    { key: 'sms', icon: Smartphone, label: t('account.smsNotif'), desc: profile?.phone ? `Recevoir un SMS au ${profile.phone}` : 'Ajoutez un numero de telephone dans votre profil', available: !!profile?.phone },
                    { key: 'push', icon: Wifi, label: t('account.pushNotif'), desc: pushSupported ? t('account.pushNotifDesc') : t('account.pushNotSupported'), available: pushSupported },
                  ].map((n) => (
                    <div key={n.key} className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${notifPrefs[n.key] && n.available ? 'border-charcoal bg-primary/5' : 'border-charcoal/10'}`} data-testid={`notif-pref-${n.key}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${notifPrefs[n.key] && n.available ? 'border-charcoal bg-primary' : 'border-charcoal/20 bg-white'}`}>
                          <n.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-sm flex items-center gap-2">
                            {n.label}
                          </p>
                          <p className="text-xs text-charcoal/50">{n.desc}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => n.available && toggleNotif(n.key)}
                        disabled={!n.available || savingNotif}
                        className={`relative w-12 h-7 rounded-full border-2 border-charcoal transition-colors ${notifPrefs[n.key] && n.available ? 'bg-secondary' : 'bg-gray-200'} ${!n.available ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                        data-testid={`notif-toggle-${n.key}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 bg-white border-2 border-charcoal rounded-full transition-transform ${notifPrefs[n.key] && n.available ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-surface-alt rounded-xl border border-charcoal/10">
                  <p className="text-xs text-charcoal/60">
                    <strong>Note :</strong> Vous pouvez aussi personnaliser les notifications pour chaque PingMe individuellement depuis votre tableau de bord, en cliquant sur un PingMe puis en modifiant ses preferences de notification.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Addresses Tab */}
            <TabsContent value="addresses">
              <div className="card-brutal">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-charcoal font-heading">Mes Adresses</h2>
                  <Dialog open={showAddressDialog} onOpenChange={(open) => {
                    setShowAddressDialog(open);
                    if (!open) {
                      setEditingAddress(null);
                      setAddressForm({ label: 'home', street: '', city: '', postal_code: '', country: 'France', is_default: false });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <button data-testid="add-address-btn" className="btn-primary text-sm px-4 py-2 flex items-center gap-2">
                        <Plus className="w-4 h-4" /> Ajouter
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-2 border-charcoal rounded-2xl shadow-brutal-xl max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-bold font-heading">
                          {editingAddress ? 'Modifier l\'adresse' : 'Nouvelle adresse'}
                        </DialogTitle>
                      </DialogHeader>
                      <form onSubmit={saveAddress} className="space-y-4 mt-4">
                        <div>
                          <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Type</label>
                          <select
                            value={addressForm.label}
                            onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                            className="input-brutal w-full"
                          >
                            <option value="home">{t('account.profile')}</option>
                            <option value="work">{t('business.contactCompany')}</option>
                            <option value="other">{t('categories.other')}</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Adresse</label>
                          <input
                            type="text"
                            value={addressForm.street}
                            onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
                            className="input-brutal w-full"
                            placeholder={t('shop.street')}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Code postal</label>
                            <input
                              type="text"
                              value={addressForm.postal_code}
                              onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                              className="input-brutal w-full"
                              placeholder={t('shop.postalCode')}
                              required
                            />
                          </div>
                          <div>
                            <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Ville</label>
                            <input
                              type="text"
                              value={addressForm.city}
                              onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                              className="input-brutal w-full"
                              placeholder={t('shop.city')}
                              required
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-[0.1em] uppercase text-charcoal mb-2 block">Pays</label>
                          <input
                            type="text"
                            value={addressForm.country}
                            onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                            className="input-brutal w-full"
                          />
                        </div>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={addressForm.is_default}
                            onChange={(e) => setAddressForm({ ...addressForm, is_default: e.target.checked })}
                            className="w-5 h-5 rounded border-2 border-charcoal"
                          />
                          <span className="text-sm text-charcoal">Adresse par défaut</span>
                        </label>
                        <button type="submit" className="w-full btn-primary">
                          {editingAddress ? 'Mettre à jour' : 'Ajouter'}
                        </button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {addresses.length === 0 ? (
                  <p className="text-charcoal/70 text-center py-8">Aucune adresse enregistrée</p>
                ) : (
                  <div className="space-y-4">
                    {addresses.map((addr) => (
                      <div
                        key={addr.address_id}
                        className={`p-4 rounded-xl border-2 ${addr.is_default ? 'border-primary bg-primary/5' : 'border-charcoal/20'}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-bold text-charcoal capitalize">
                                {addr.label === 'home' ? 'Domicile' : addr.label === 'work' ? 'Travail' : 'Autre'}
                              </span>
                              {addr.is_default && (
                                <span className="bg-primary text-charcoal text-xs font-bold px-2 py-1 rounded-full">Par défaut</span>
                              )}
                            </div>
                            <p className="text-charcoal/70">{addr.street}</p>
                            <p className="text-charcoal/70">{addr.postal_code} {addr.city}</p>
                            <p className="text-charcoal/70">{addr.country}</p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingAddress(addr);
                                setAddressForm(addr);
                                setShowAddressDialog(true);
                              }}
                              className="p-2 rounded-full border-2 border-charcoal/20 hover:border-charcoal"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deleteAddress(addr.address_id)}
                              className="p-2 rounded-full border-2 border-charcoal/20 hover:border-danger text-danger"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <div className="card-brutal">
                <h2 className="text-xl font-bold text-charcoal font-heading mb-6">Historique des commandes</h2>

                {orders.length === 0 ? (
                  <div className="text-center py-8">
                    <Package className="w-16 h-16 text-charcoal/20 mx-auto mb-4" />
                    <p className="text-charcoal/70">Aucune commande pour le moment</p>
                    <Link to="/shop" className="btn-primary mt-4 inline-block">Voir la boutique</Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <Link
                        key={order.order_id}
                        to={`/orders/${order.order_id}`}
                        className="block p-4 rounded-xl border-2 border-charcoal/20 hover:border-charcoal transition-colors"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-charcoal">#{order.order_id.replace('order_', '').toUpperCase()}</p>
                            <p className="text-sm text-charcoal/60">{formatDate(order.created_at)}</p>
                            <p className="text-sm text-charcoal/70 mt-1">
                              {order.items?.length || 0} article{(order.items?.length || 0) > 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-charcoal">{order.total?.toFixed(2)}€</p>
                            <span className={`inline-block mt-1 text-xs font-bold px-2 py-1 rounded-full border-2 border-charcoal ${getStatusColor(order.status)}`}>
                              {getStatusLabel(order.status)}
                            </span>
                          </div>
                          <ChevronRight className="w-5 h-5 text-charcoal/40 ml-2" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {/* Legal Links */}
          <div className="mt-8 pt-8 border-t-2 border-charcoal/10">
            <h3 className="font-bold text-charcoal mb-4">Informations légales</h3>
            <div className="flex flex-wrap gap-4">
              <Link to="/legal" className="text-charcoal/70 hover:text-charcoal flex items-center gap-2">
                <FileText className="w-4 h-4" /> Mentions légales
              </Link>
              <Link to="/privacy" className="text-charcoal/70 hover:text-charcoal flex items-center gap-2">
                <FileText className="w-4 h-4" /> Politique de confidentialité
              </Link>
              <Link to="/terms" className="text-charcoal/70 hover:text-charcoal flex items-center gap-2">
                <FileText className="w-4 h-4" /> CGV
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Account;
